import { inject, Injectable, signal } from '@angular/core';
import {
  patientActions,
  selectCurrentUserProfile,
  selectExtractedTeeth,
  selectPatientPrimaryProvider,
  SelectProviderService,
  selectSelectedPatient,
  selectSelectedPatientId,
  selectTeethState,
  selectUserProvider,
  selectUserSite,
} from '@libs/store';
import { PerioMeasurement, PerioMeasurementsBySideDraft, Tooth, ToothPerioDraft, ToothSide } from '@libs/ui-lib';
import { DataState, deepMergeObjectVersions, FfdComponentStore, isNotNull, routes, SiteFeatureFlag, toDateFormat } from '@libs/utils';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  Observable,
  of,
  skip,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { AddPerioExamRequest, PerioDataService, PerioExamTooth, Provider, UpdatePerioExamRequest } from '@ffd/api-client';
import {
  AudioProcessingService,
  FileUploadService,
  PerioExamLocalStorageService,
  PerioExamSiteAndPatientListenerService,
  ToastService,
} from '@libs/shared-services';
import { PerioTooltipToothSurface } from '../../../perio-add-treatment-screen/perio-exam-toolbar/perio-exam-toolbar.interface';
import { PerioJawMeasurements } from '../../../perio-active-tooth-form/components/perio-jaw-related-measurements/perio-jaw-related-measurements.component';
import { PerioFormControlService } from '../../../perio-active-tooth-form/services/perio-form-control/perio-form-control.service';
import { TargetIndexMovement } from '../../../perio-active-tooth-form/components/perio-config/perio-config.vm';
import { PerioJawConfig } from '../../../perio-jaw/perio-jaw.interface';

import { PerioTextToSpeechService } from './perio-text-to-speech.service';
import { perioExamJawConfigMap } from './constants';
import {
  MeasurementField,
  PerioExamState,
  PerioExamTraversalStrategy,
  PerioExamTraversalStrategyStep,
  PerioExamViewModel,
  PerioJawConfigTuple,
  PerioTeethRecord,
  RecessionInputType,
  Surface,
  TOOTH_POSITIONS_WITH_FG,
  TOOTH_POSITIONS_WITH_FG_AS_MESIAL_AND_DISTAL,
} from './models';
import { initialState } from './state';
import {
  deriveValidToothListFromTeethRecord,
  generateTooltipToothSurfaceList,
  getJawConfigForToothPosition,
  getNextToothIndex,
  getPerioExamTraversalStrategy,
  getPreviousToothIndex,
  getSiblingTeethForViewModel,
  getToothForJawVisualisation,
  getToothPositionsFilteredByExtracted,
  isReversedMeasurementOrder,
  mapToothPerioDraftToPerioExamTooth,
} from './utils';
import { VoicePerioService } from './voice-perio.service';

@Injectable({ providedIn: 'root' })
export class PerioExamScreenService extends FfdComponentStore<PerioExamState, PerioExamViewModel> {
  constructor() {
    super(structuredClone(initialState));
  }

  private readonly store = inject(Store);
  private readonly localStorageService = inject(PerioExamLocalStorageService);
  private readonly providerService = inject(SelectProviderService);
  private readonly perioDataService = inject(PerioDataService);
  private readonly perioFormControlService = inject(PerioFormControlService);
  private readonly siteAndPatientListenerService = inject(PerioExamSiteAndPatientListenerService);
  private readonly perioTextToSpeechService = inject(PerioTextToSpeechService);
  private readonly voicePerioService = inject(VoicePerioService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly toastService = inject(ToastService);
  private readonly audioProcessingService = inject(AudioProcessingService);

  private readonly initialized$ = new BehaviorSubject(false);

  private readonly siteId = this.siteAndPatientListenerService.getSiteId();
  private readonly patientId = this.siteAndPatientListenerService.getPatientId();
  public readonly hasDraft$ = this.localStorageService.hasDraft$;
  public readonly perioSavingInProgress = signal<boolean>(false);

  public shouldMoveToNextToothOnNextPocketDepth = false;
  public shouldMoveToNextToothOnNextRecession = false;

  public override init(perioExam?: PerioExamState): void {
    const siteId = this.siteId()!;
    const patientId = this.patientId()!;

    const teethStateSignal = this.store.selectSignal(selectTeethState);
    if (![DataState.Loading, DataState.Fetched].includes(teethStateSignal())) {
      this.store.dispatch(patientActions.onLoadTeeth({ siteId, patientId, examCount: 5, silentReload: false }));
    }
    const profileId = this.store.selectSignal(selectCurrentUserProfile)()?.id || '';

    this.localStorageService.init(profileId);

    const defaultStrategy = this.localStorageService.getDefaultTraversalStrategy();
    const defaultProviderId = this.getDefaultProviderId();
    const defaultRecessionInputType = this.localStorageService.getDefaultRecessionInputType();

    const providerIdPatchObj = isNotNull(defaultProviderId) ? { providerId: defaultProviderId } : {};
    const defaultTraversalStrategyPatchObj = isNotNull(defaultStrategy) ? { traversalStrategy: defaultStrategy } : {};

    this.patchState({ ...providerIdPatchObj, ...defaultTraversalStrategyPatchObj, recessionInputType: defaultRecessionInputType });

    if (perioExam) {
      this.setState(perioExam);
      this.localStorageService.saveDraft(perioExam);
    } else if (this.hasDraft()) {
      const draft = this.localStorageService.retrieveDraft();
      this.setState(draft!);
    } else {
      this.setState(structuredClone(initialState));
      const extractedTeethPositions$ = this.store.select(selectExtractedTeeth).pipe(filter(Boolean), first());

      extractedTeethPositions$.subscribe(this.setExtractedTootPositions.bind(this));
    }

    this.setLoadedFlagWhenDependenciesLoaded();

    this.initialized$.next(true);
  }

  /**
   * Initialize voice perio service with callbacks
   * Called by the component that uses voice functionality
   */
  public initVoicePerioService(): void {
    this.voicePerioService.init({
      applyMeasurement: this.applyMeasurement.bind(this),
      applyMeasurementAddendum: this.applyMeasurementAddendum.bind(this),
      moveToPosition: this.moveToPosition.bind(this),
      moveToSide: this.moveToSide.bind(this),
      markToothAsMissing: this.markToothAsMissing.bind(this),
      clearMeasurements: this.clearMeasurements.bind(this),
      setInputMode: this.setInputMode.bind(this),
      getInputMode: () => this.perioFormControlService.getInputMode(),
    });
    // Sync current input mode with voice perio service
    const currentMode = this.perioFormControlService.getInputMode();
    this.voicePerioService.setCurrentInputMode(currentMode);
  }

  /**
   * Sets the input mode for voice perio (pocket depth or recession)
   * @param mode - The input mode to set
   */
  private setInputMode(mode: 'pocketDepth' | 'recession'): void {
    this.perioFormControlService.setInputMode(mode);
    this.voicePerioService.setCurrentInputMode(mode);
  }

  public isInitializationComplete(): boolean {
    return this.initialized$.value;
  }

  public updateTeeth(siteId: string, patientId: string): void {
    this.store.dispatch(patientActions.onLoadTeeth({ siteId, patientId, examCount: 5, silentReload: true }));
  }

  /**
   * Applies a pocket depth value programmatically, simulating user input
   * @param pocketDepth - The pocket depth value to apply (1-15)
   * @param surface - The surface to apply to ('mesial', 'central', 'distal'). If not provided, applies to current active surface
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  public applyMeasurement(
    pocketDepth?: number,
    bop?: boolean,
    sop?: boolean,
    mobility?: number,
    furcation?: number,
    recession?: number,
  ): void {
    const MAX_POCKET_DEPTH = 15;
    if (pocketDepth && (pocketDepth < 1 || pocketDepth > MAX_POCKET_DEPTH)) {
      console.warn(`Pocket depth value must be between 1 and ${MAX_POCKET_DEPTH}`);
      return;
    }
    if (this.shouldMoveToNextToothOnNextPocketDepth && pocketDepth) {
      this.perioFormControlService.removePreservedDigit();
      this.moveToNextTooth();
    }
    if (this.shouldMoveToNextToothOnNextRecession && recession !== undefined) {
      this.perioFormControlService.removePreservedDigit();
      this.moveToNextTooth();
    }
    queueMicrotask(() => {
      const currentPosition = this.state().currentToothPosition.position;
      const side = this.state().currentToothPosition.side;
      const isReversed = isReversedMeasurementOrder(currentPosition, side);

      const measurementOrder = this.perioFormControlService.getMeasurementOrder(isReversed);
      const defaultSurface = measurementOrder[0];
      const targetPocketDepthSurface = this.perioFormControlService.getActivePocketDepthCursor() || defaultSurface;
      const isLastPocketDepthMeasurement = targetPocketDepthSurface === measurementOrder.at(-1);

      //Advance to next surface after setting the value
      const ADVANCE_DELAY_MS = 10;
      setTimeout(() => {
        // Focus on the target surface and set the value
        if (pocketDepth) {
          this.perioFormControlService.focusPocketDepth(targetPocketDepthSurface, isReversed, TargetIndexMovement.None, pocketDepth);
          this.perioFormControlService.setActivePocketDepthCursor(targetPocketDepthSurface, true, isReversed);
        }
        // If bleeding is specified, trigger the bleeding event
        if (bop) {
          //if both pocketDepth and bop are provided, use targetSurface for bleeding
          const targetBleedingSurface = pocketDepth
            ? targetPocketDepthSurface
            : this.perioFormControlService.getActiveCursor() || defaultSurface;
          this.perioFormControlService.triggerBleedingOnSurface(targetBleedingSurface);
        }

        if (sop) {
          //if both pocketDepth and bop are provided, use targetSurface for suppuration
          const targetSuppurationSurface = pocketDepth
            ? targetPocketDepthSurface
            : this.perioFormControlService.getActiveCursor() || defaultSurface;
          this.perioFormControlService.triggerSuppurationOnSurface(targetSuppurationSurface);
        }

        if (mobility !== undefined) {
          this.perioFormControlService.setMobilityValue(mobility);
        }

        // Only apply furcation if the current tooth supports it
        if (furcation !== undefined && this.hasFurcation(currentPosition)) {
          this.perioFormControlService.setFurcationValue(furcation);
        }

        if (recession !== undefined) {
          const currentMode = this.perioFormControlService.getInputMode();
          const isCentralRecession = this.isCentralRecession();

          if (isCentralRecession) {
            // Set cursor to central to target the correct field
            this.perioFormControlService.setActiveRecessionCursor('central');
            this.perioFormControlService.setActiveCursor('central');

            if (currentMode === 'recession') {
              this.shouldMoveToNextToothOnNextRecession = true;
              // In recession mode, we want to focus the field, so emit the value and keep cursor on central
              queueMicrotask(() => {
                this.perioFormControlService.setRecessionValue(recession);
              });
            } else {
              // In pocket depth mode, just set the value without disturbing focus
              const previousActiveCursor = this.perioFormControlService.getActiveCursor();
              const previousActiveRecessionCursor = this.perioFormControlService.getActiveRecessionCursor();

              queueMicrotask(() => {
                this.perioFormControlService.setRecessionValue(recession);
                // Restore previous cursor state to preserve user's focus
                queueMicrotask(() => {
                  this.perioFormControlService.setActiveCursor(previousActiveCursor);
                  this.perioFormControlService.setActiveRecessionCursor(previousActiveRecessionCursor);
                });
              });
            }
          } else {
            // Full recession mode: work through surfaces like pocket depth
            const activeRecessionCursor = this.perioFormControlService.getActiveRecessionCursor();
            const targetRecessionSurface = activeRecessionCursor || defaultSurface;
            const isLastRecessionMeasurement = targetRecessionSurface === measurementOrder.at(-1);

            if (currentMode === 'recession') {
              // In recession mode, focus the field and work through surfaces
              this.perioFormControlService.setActiveRecessionCursor(targetRecessionSurface);
              //this.perioFormControlService.setActiveCursor(targetRecessionSurface);

              queueMicrotask(() => {
                this.perioFormControlService.setRecessionValue(recession);

                // Advance to next surface after setting value
                queueMicrotask(() => {
                  const currentIndex = measurementOrder.indexOf(targetRecessionSurface);
                  const nextIndex = currentIndex + 1;
                  const nextSurface = nextIndex < measurementOrder.length ? measurementOrder[nextIndex] : null;
                  this.perioFormControlService.setActiveRecessionCursor(nextSurface);

                  // Update active cursor for visual feedback if not at the last surface
                  if (nextSurface !== null) {
                    this.perioFormControlService.setActiveCursor(nextSurface);
                  }

                  // // Set flag if this is the last surface
                  // this.shouldMoveToNextToothOnNextRecession = isLastRecessionMeasurement;
                  if (isLastRecessionMeasurement) {
                    this.moveToNextTooth();
                    const firstSurface = measurementOrder[0];
                    this.perioFormControlService.setActiveRecessionCursor(firstSurface);
                    this.perioFormControlService.setActiveCursor(firstSurface);
                  }
                });
              });
            } else {
              // In pocket depth mode, apply to current active cursor surface (same as central recession behavior)
              // Use the active cursor (where the user is currently focused) instead of always using central
              const targetSurface = this.perioFormControlService.getActiveCursor() || defaultSurface;

              // Set cursors to target surface to apply the recession value
              this.perioFormControlService.setActiveRecessionCursor(targetSurface);
              this.perioFormControlService.setActiveCursor(targetSurface);

              const previousActiveCursor = this.perioFormControlService.getActiveCursor();
              const previousActiveRecessionCursor = this.perioFormControlService.getActiveRecessionCursor();

              queueMicrotask(() => {
                this.perioFormControlService.setRecessionValue(recession);
                // Restore previous cursor state to preserve user's focus
                queueMicrotask(() => {
                  this.perioFormControlService.setActiveCursor(previousActiveCursor);
                  this.perioFormControlService.setActiveRecessionCursor(previousActiveRecessionCursor);
                });
              });
            }
          }
        }

        queueMicrotask(() => {
          this.saveDraft();
        });
      }, ADVANCE_DELAY_MS);

      if (pocketDepth) {
        this.shouldMoveToNextToothOnNextPocketDepth = isLastPocketDepthMeasurement ? true : false;
      }
    });
  }

  public isInitializationComplete$(): Observable<boolean> {
    return this.initialized$.asObservable();
  }

  public applyPerioSideChange(change: PerioMeasurementsBySideDraft): void {
    const {
      currentToothPosition,
      teethRecord: prevTeeth,
      completedSteps: previousCompletedSteps,
      touchedSteps: preivousTouchedSteps,
    } = this.state();
    const { side, position } = currentToothPosition;

    const completedSteps = this.isAllSurfacePocketDepthCompleted(change)
      ? [...previousCompletedSteps, currentToothPosition]
      : [...previousCompletedSteps];

    const touchedSteps = this.isSomeSurfacePocketDepthCompleted(change)
      ? [...preivousTouchedSteps, currentToothPosition]
      : [...preivousTouchedSteps];

    const currentTooth = {
      ...prevTeeth[position],
      perio: {
        ...prevTeeth[position].perio,
        [side]: deepMergeObjectVersions({ ...prevTeeth[position].perio[side] }, change, true),
      },
    };

    const teethRecord: PerioTeethRecord = {
      ...prevTeeth,
      [position]: currentTooth,
    };

    this.patchStateAndSaveDraft({ teethRecord, completedSteps, touchedSteps });
  }

  /**
   * Updates the perio exam traversal strategy and recalculates the derived `toothOrder`.
   *
   * @param traversalStrategy - Strategy selected by the user via UI.
   */
  public updatePerioExamTraversalStrategy(traversalStrategy: PerioExamTraversalStrategy): void {
    const jawConfig = perioExamJawConfigMap[traversalStrategy];
    const allMissingTeethPositions = this.getAllMissingTeethPositions();
    const toothOrderWithExtractedIncluded = getPerioExamTraversalStrategy(jawConfig);
    const { toothOrder, currentToothPosition: defaultPosition } = getToothPositionsFilteredByExtracted(
      allMissingTeethPositions,
      toothOrderWithExtractedIncluded,
    );

    // Preserve current tooth position if it's still valid in the new order
    const currentPosition = this.state().currentToothPosition;
    const isCurrentPositionStillValid = toothOrder.some(
      (step) => step.position === currentPosition.position && step.side === currentPosition.side,
    );
    const currentToothPosition = isCurrentPositionStillValid ? currentPosition : defaultPosition;

    this.patchStateAndSaveDraft({
      toothOrder,
      currentToothPosition,
      traversalStrategy,
    });
  }

  private markToothAsMissing(toothPosition: number): void {
    const { missingTeethPositions } = this.state();
    if (missingTeethPositions.includes(toothPosition)) {
      return;
    }
    const updatedMissingTeethPositions = [...missingTeethPositions, toothPosition].sort((a, b) => a - b);
    this.updateMissingTeeth(updatedMissingTeethPositions);
  }

  /**
   * Checks if a tooth position supports furcation measurements
   * @param toothPosition - The tooth position to check
   * @returns true if the tooth supports furcation, false otherwise
   */
  private hasFurcation(toothPosition: number): boolean {
    return TOOTH_POSITIONS_WITH_FG.includes(toothPosition);
  }

  /**
   * Clears specific measurements from teeth/sides/surfaces
   * @param toothPosition - The tooth to clear (null = current tooth)
   * @param side - The side(s) to clear (null = current side, 'both' = both sides)
   * @param surfaces - The surfaces to clear (null = all surfaces)
   * @param measurements - The measurements to clear (null = all measurements)
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private clearMeasurements(
    toothPosition: number | null,
    side: 'buccal' | 'lingual' | 'both' | null,
    surfaces: Surface[] | null,
    measurements: MeasurementField[] | null,
  ): void {
    const { teethRecord, currentToothPosition } = this.state();

    // Determine target tooth position
    const targetPosition = toothPosition ?? currentToothPosition.position;
    const tooth = teethRecord[targetPosition];

    if (!tooth) {
      console.warn(`Cannot clear measurements: tooth ${targetPosition} not found`);
      return;
    }

    // Determine which sides to clear
    const sidesToClear: ToothSide[] = side === 'both' ? ['buccal', 'lingual'] : side ? [side] : [currentToothPosition.side];

    // Determine which surfaces to clear (default: all)
    const surfacesToClear = surfaces ?? [Surface.Distal, Surface.Central, Surface.Mesial];

    // Map Surface enum values to property keys
    const surfaceKeys: Array<'distal' | 'central' | 'mesial'> = surfacesToClear.map((s) =>
      s === Surface.Distal ? 'distal' : s === Surface.Central ? 'central' : 'mesial',
    );

    // Determine which measurements to clear (default: all)
    const measurementsToDelete = measurements ?? ['depth', 'bop', 'suppuration', 'mobility', 'furcation', 'recession'];

    // Build updated tooth
    const updatedTooth: ToothPerioDraft = structuredClone(tooth);

    // Handle tooth-level measurements
    if (measurementsToDelete.includes('mobility')) {
      updatedTooth.mobility = 0;
    }

    // Handle per-side measurements
    this.clearPerioSideMeasurements(updatedTooth, sidesToClear, surfaceKeys, measurementsToDelete);

    // If clearing the active tooth's active side, use applyPerioSideChange for proper form sync
    // The measurements effect in perio-config.component.ts will handle form synchronization
    if (targetPosition === currentToothPosition.position && sidesToClear.includes(currentToothPosition.side)) {
      const clearedSideMeasurements = updatedTooth.perio[currentToothPosition.side];
      this.applyPerioSideChange(clearedSideMeasurements!);
    } else {
      // For non-active teeth or sides, just update the state
      const updatedTeethRecord: PerioTeethRecord = {
        ...teethRecord,
        [targetPosition]: updatedTooth,
      };
      this.patchStateAndSaveDraft({ teethRecord: updatedTeethRecord });
    }
    this.moveToPosition(targetPosition, false, surfacesToClear.length === 1 ? surfacesToClear[0] : null);
  }

  private clearPerioSideMeasurements(
    tooth: ToothPerioDraft,
    sidesToClear: ToothSide[],
    surfaceKeys: Array<'distal' | 'central' | 'mesial'>,
    measurementsToDelete: MeasurementField[],
  ): void {
    for (const sideKey of sidesToClear) {
      const sideMeasurements = tooth.perio[sideKey];
      if (!sideMeasurements) continue;

      for (const measurementKey of measurementsToDelete) {
        if (measurementKey === 'mobility') continue; // Already handled at tooth level

        const fieldName = this.mapMeasurementFieldToProperty(measurementKey);
        if (!fieldName) continue;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currentValue = (sideMeasurements as any)[fieldName];
        if (!currentValue) continue;

        this.clearSurfaceValues(currentValue as PerioMeasurement<number | boolean>, surfaceKeys);
        this.clearRelatedMeasurements(measurementKey, sideMeasurements, surfaceKeys);
      }
    }
  }

  private clearRelatedMeasurements(
    measurementKey: MeasurementField,
    sideMeasurements: PerioMeasurementsBySideDraft,
    surfaceKeys: Array<'distal' | 'central' | 'mesial'>,
  ): void {
    // When clearing pocket depth, also clear clinical attachment loss as a side effect
    if (measurementKey === 'depth' && sideMeasurements.clinicalAttachmentLoss) {
      this.clearSurfaceValues(sideMeasurements.clinicalAttachmentLoss as PerioMeasurement<number | boolean>, surfaceKeys);
    }
  }

  private clearSurfaceValues(measurement: PerioMeasurement<number | boolean>, surfaceKeys: Array<'distal' | 'central' | 'mesial'>): void {
    for (const surfaceKey of surfaceKeys) {
      if (typeof measurement[surfaceKey] === 'boolean') {
        (measurement[surfaceKey] as boolean) = false;
      } else if (typeof measurement[surfaceKey] === 'number') {
        (measurement[surfaceKey] as number) = 0;
      }
    }
  }

  /**
   * Maps MeasurementField names to PerioMeasurementsBySide property names
   */
  private mapMeasurementFieldToProperty(field: MeasurementField): string | null {
    const map: Record<MeasurementField, string | null> = {
      depth: 'pocketDepth',
      bop: 'bleedingOnProbing',
      suppuration: 'suppuration',
      furcation: 'furcation',
      recession: 'gingivalMargin',
      mobility: null, // Handled separately at tooth level
    };
    return map[field] ?? null;
  }

  /**
   * Applies measurements to a specific tooth/side/surfaces without moving the cursor
   * Similar to clearMeasurements but applies values instead of clearing them
   * @param toothPosition - The tooth to apply measurements to (null = current tooth)
   * @param side - The side to apply measurements to (null = current side)
   * @param surfaces - The surfaces to apply to (null = active cursor surface for current tooth)
   * @param depth - Pocket depth value
   * @param bop - Bleeding on probing
   * @param suppuration - Suppuration flag
   * @param mobility - Mobility value
   * @param furcation - Furcation value
   * @param recession - Recession/gingival margin value
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private applyMeasurementAddendum(
    toothPosition: number | null,
    side: 'buccal' | 'lingual' | null,
    surfaces: Surface[] | null,
    depth: number | null,
    bop: boolean | null,
    suppuration: boolean | null,
    mobility: number | null,
    furcation: number | null,
    recession: number | null,
  ): void {
    const { teethRecord, currentToothPosition } = this.state();

    // Determine target tooth position and side
    const targetPosition = toothPosition ?? currentToothPosition.position;
    const targetSide = side ?? currentToothPosition.side;

    const tooth = teethRecord[targetPosition];

    if (!tooth) {
      console.warn(`Cannot apply measurement addendum: tooth ${targetPosition} not found`);
      return;
    }

    // Check if we're targeting the current tooth
    const isCurrentTooth = targetPosition === currentToothPosition.position && targetSide === currentToothPosition.side;

    // Determine which surfaces to apply to
    let surfacesToApply: Surface[];
    if (surfaces !== null) {
      // Explicitly specified surfaces
      surfacesToApply = surfaces;
    } else if (isCurrentTooth) {
      // For current tooth, use active cursor surface if no surfaces specified
      const activeCursor = this.perioFormControlService.getActiveCursor();
      if (activeCursor === null) {
        surfacesToApply = [Surface.Distal, Surface.Central, Surface.Mesial];
      } else {
        // Convert PerioMeasurementSurface (string) to Surface enum
        const surfaceMap: Record<string, Surface> = {
          distal: Surface.Distal,
          central: Surface.Central,
          mesial: Surface.Mesial,
        };
        surfacesToApply = [surfaceMap[activeCursor]];
      }
    } else {
      // For different tooth without explicit surfaces, skip bleeding/suppuration but allow depth/furcation/mobility
      // These will be handled separately below
      surfacesToApply = [];
    }

    // Build updated tooth
    const updatedTooth: ToothPerioDraft = structuredClone(tooth);

    // Handle tooth-level measurements
    if (mobility !== null) {
      updatedTooth.mobility = mobility;
    }

    // Handle per-side measurements
    const sideMeasurements = updatedTooth.perio[targetSide];
    if (!sideMeasurements) {
      console.warn(`Cannot apply measurement addendum: side ${targetSide} not found for tooth ${targetPosition}`);
      return;
    }

    // Apply measurements to specified surfaces
    // Only apply if we have surfaces to apply to (except for mobility which is tooth-level)
    if (depth !== null && sideMeasurements.pocketDepth && surfacesToApply.length > 0) {
      for (const surface of surfacesToApply) {
        sideMeasurements.pocketDepth[surface] = depth;
        //apply the CAL as well, since it's derived from PD + recession, you need to take into account that maybe recession is alreay set
        if (sideMeasurements.clinicalAttachmentLoss && sideMeasurements.gingivalMargin) {
          sideMeasurements.clinicalAttachmentLoss[surface] = depth + (sideMeasurements.gingivalMargin[surface] ?? 0);
        }
      }
    }

    // Only apply bleeding/suppuration if we have surfaces to apply to
    // This prevents applying to all surfaces when targeting a different tooth without explicit surfaces
    if (bop !== null && sideMeasurements.bleedingOnProbing && surfacesToApply.length > 0) {
      for (const surface of surfacesToApply) {
        sideMeasurements.bleedingOnProbing[surface] = bop;
      }
    }

    if (suppuration !== null && sideMeasurements.suppuration && surfacesToApply.length > 0) {
      for (const surface of surfacesToApply) {
        sideMeasurements.suppuration[surface] = suppuration;
      }
    }

    // Only apply furcation if the target tooth supports it
    if (furcation !== null && sideMeasurements.furcation && surfacesToApply.length > 0 && this.hasFurcation(targetPosition)) {
      for (const surface of surfacesToApply) {
        sideMeasurements.furcation[surface] = furcation;
      }
    }

    if (recession !== null && sideMeasurements.gingivalMargin && sideMeasurements.clinicalAttachmentLoss) {
      const isCentralRecession = this.isCentralRecession();

      if (isCentralRecession) {
        // Central-only recession mode: always apply to central surface
        sideMeasurements.gingivalMargin[Surface.Central] = recession;
        sideMeasurements.clinicalAttachmentLoss[Surface.Central] = (sideMeasurements.pocketDepth?.[Surface.Central] ?? 0) + recession;
      } else if (surfacesToApply.length > 0) {
        // All recession mode: apply to the specified surfaces
        for (const surface of surfacesToApply) {
          sideMeasurements.gingivalMargin[surface] = recession;
          sideMeasurements.clinicalAttachmentLoss[surface] = (sideMeasurements.pocketDepth?.[surface] ?? 0) + recession;
        }
      }
    }

    const updatedTeethRecord: PerioTeethRecord = {
      ...teethRecord,
      [targetPosition]: updatedTooth,
    };
    this.patchStateAndSaveDraft({ teethRecord: updatedTeethRecord });

    //the reason why we don't want to move the cursor if we're on the current tooth and side is that we want to keep the focused surface
    if (targetPosition !== currentToothPosition.position || targetSide !== currentToothPosition.side) {
      this.moveToSide(targetSide);
      this.moveToPosition(targetPosition, false, null);
    }
  }

  public updateMissingTeeth(missingTeethPositions: number[]): void {
    const { traversalStrategy, currentToothPosition: currentPosition } = this.state();
    const jawConfig = perioExamJawConfigMap[traversalStrategy];
    const allMissingTeethPositions = this.getAllMissingTeethPositions(missingTeethPositions);
    const perioExamToothOrder = getPerioExamTraversalStrategy(jawConfig);
    const { toothOrder, currentToothPosition: defaultPosition } = getToothPositionsFilteredByExtracted(
      allMissingTeethPositions,
      perioExamToothOrder,
    );

    // Preserve current tooth position if it's still valid in the new order
    const isCurrentPositionStillValid = toothOrder.some(
      (step) => step.position === currentPosition.position && step.side === currentPosition.side,
    );
    const currentToothPosition = isCurrentPositionStillValid ? currentPosition : defaultPosition;

    this.patchStateAndSaveDraft({
      missingTeethPositions,
      toothOrder,
      currentToothPosition,
    });
  }

  public applyJawMeasurementChange({ mob: mobility, fg }: PerioJawMeasurements): void {
    const toothKey = this.state().currentToothPosition.position;
    const tooth: ToothPerioDraft = {
      ...this.state().teethRecord[toothKey],
      mobility,
    };

    if (TOOTH_POSITIONS_WITH_FG_AS_MESIAL_AND_DISTAL.includes(tooth.position)) {
      tooth.perio.buccal!.furcation!.mesial = fg;
    } else {
      tooth.perio.buccal!.furcation!.central = fg;
    }

    const teethRecord = {
      ...this.state().teethRecord,
      [toothKey]: tooth,
    };

    this.patchStateAndSaveDraft({ teethRecord });
  }

  public moveToNextTooth(): void {
    this.moveToTooth('next');
  }

  public moveToPreviousTooth(): void {
    this.moveToTooth('prev');
  }

  public switchToLingual(): void {
    this.moveToSide('lingual');
  }

  public switchToFacial(): void {
    this.moveToSide('buccal');
  }

  public navigateToToothAndSide(toothNumber: number, side: ToothSide): void {
    this.shouldMoveToNextToothOnNextPocketDepth = false;
    this.shouldMoveToNextToothOnNextRecession = false;
    this.perioFormControlService.removePreservedDigit();

    const { toothOrder: perioExamToothOrder } = this.state();
    const targetPosition = perioExamToothOrder.find((step) => step.position === toothNumber && step.side === side);

    if (targetPosition) {
      this.patchStateAndSaveDraft({ currentToothPosition: targetPosition });
      this.focusOnFirstSurface(targetPosition);
    }
  }

  public hasDraft(): boolean {
    return this.localStorageService.hasDraft();
  }

  public getDraft(): PerioExamState | null {
    return this.localStorageService.retrieveDraft();
  }

  public removeDraft(): void {
    this.localStorageService.removeDraft();
  }

  public reset(): void {
    this.removeDraft();
    this.perioTextToSpeechService.stopTranscription();
    this.setState(structuredClone(initialState));
    this.init();
  }

  public getDefaultProviderId(): string | null {
    const defaultProviderId = this.getDefaultProvider()?.id ?? null;
    const storedDefaultProviderId = this.localStorageService.getDefaultProviderId();
    return storedDefaultProviderId || defaultProviderId;
  }

  public setDefaultProvider(provider: Provider): void {
    this.localStorageService.setDefaultProvider(provider.id);
    this.updatePerioExamProvider(provider);
  }

  public getDefaultTraversalStrategy(): PerioExamTraversalStrategy | null {
    return this.localStorageService.getDefaultTraversalStrategy();
  }

  public setDefaultTraversalStrategy(strategy: PerioExamTraversalStrategy): void {
    this.localStorageService.setDefaultTraversalStragegy(strategy);
    this.updatePerioExamTraversalStrategy(strategy);
  }

  public getDefaultRecessionInputType(): RecessionInputType {
    // Return current value from state if available, otherwise fall back to localStorage
    // This ensures the settings modal shows the active value, not just the saved default
    const currentState = this.state();
    return currentState.recessionInputType ?? this.localStorageService.getDefaultRecessionInputType();
  }

  public isCentralRecession(): boolean {
    // Read from viewModel to ensure reactivity - viewModel is a signal that updates when state changes
    return this.viewModel().recessionInputType === 'central';
  }

  public setDefaultRecessionInputType(recessionInputType: RecessionInputType): void {
    this.localStorageService.setDefaultRecessionInputType(recessionInputType);
    // Use setState to ensure the state is fully updated and signals recompute
    const currentState = this.state();
    this.setState({
      ...currentState,
      recessionInputType,
    });
    // Save the updated state as draft synchronously to prevent race conditions
    this.saveDraft();
  }

  protected override mapStateToViewModel(state: PerioExamState): PerioExamViewModel {
    const {
      currentToothPosition,
      loaded,
      traversalStrategy,
      teethRecord,
      toothOrder,
      extractedTeethPositions,
      missingTeethPositions,
      touchedSteps,
      cheatSheetOpened,
      isEditing,
      recessionInputType,
    } = state;

    const jawConfig = perioExamJawConfigMap[traversalStrategy];
    const activeJawConfig = getJawConfigForToothPosition(jawConfig, currentToothPosition.position, currentToothPosition.side);
    const providerId = state.providerId || this.getDefaultProviderId();

    const teeth = deriveValidToothListFromTeethRecord(teethRecord);
    const currentTooth = teethRecord[currentToothPosition.position] as Tooth;
    const isReversedVisualDirection = activeJawConfig.visualDirection === 'left';

    const { previousTooth, previousToothPosition, previousToothIndex, nextTooth, nextToothPosition, nextToothIndex } =
      getSiblingTeethForViewModel(teethRecord, toothOrder, currentToothPosition);

    const isFirstToothInExam = this.compareTraversalStrategySteps(currentToothPosition, toothOrder[0]);
    const isLastToothInExam = this.compareTraversalStrategySteps(currentToothPosition, structuredClone(toothOrder).pop()!);

    const activeJaw = activeJawConfig.jaw;
    const { firstPositionInActiveJaw, lastPositionInActiveJaw } = this.getActiveJawBoundaryPositions(toothOrder, jawConfig, activeJaw);

    const isFirstToothActive = firstPositionInActiveJaw !== null && currentToothPosition.position === firstPositionInActiveJaw;
    const isLastToothActive = lastPositionInActiveJaw !== null && currentToothPosition.position === lastPositionInActiveJaw;

    const currentToothForJawVisualisation = getToothForJawVisualisation(currentTooth);
    const previousToothForJawVisualisation = previousToothIndex < toothOrder.length - 1 ? getToothForJawVisualisation(previousTooth) : null;
    const nextToothForJawVisualisation = nextToothIndex > 0 ? getToothForJawVisualisation(nextTooth) : null;

    const previousToothForJawVisualisationResolved = isReversedVisualDirection
      ? nextToothForJawVisualisation
      : previousToothForJawVisualisation;
    const nextToothForJawVisualisationResolved = isReversedVisualDirection
      ? previousToothForJawVisualisation
      : nextToothForJawVisualisation;
    const indicatorToothSurfaceList: PerioTooltipToothSurface[] = generateTooltipToothSurfaceList(state);
    const provider = this.providerService.getProviderById(providerId!) || this.getDefaultProvider();

    const allTeethTouched = toothOrder.every((orderStep) =>
      touchedSteps.some((touchedStep) => touchedStep.position === orderStep.position && touchedStep.side === orderStep.side),
    );

    const oneToothTouched = toothOrder.some((orderStep) =>
      touchedSteps.some((touchedStep) => touchedStep.position === orderStep.position && touchedStep.side === orderStep.side),
    );

    const userSite = this.store.selectSignal(selectUserSite);
    const profileId = this.store.selectSignal(selectCurrentUserProfile)()?.id || '';

    const addPerioExamFeatureEnabled =
      userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.AddPerioExam)?.isEnabled || false;

    const editPerioExamFeatureEnabled =
      userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.EditPerioExam)?.isEnabled || false;

    const vm: PerioExamViewModel = {
      currentToothPosition,
      nextToothPosition,
      previousToothPosition,
      loaded,
      traversalStrategy,
      teeth,
      teethRecord,
      indicatorToothSurfaceList,
      jawConfig: activeJawConfig,
      currentToothForJawVisualisation,
      currentTooth,
      previousToothForJawVisualisation: previousToothForJawVisualisationResolved,
      nextToothForJawVisualisation: nextToothForJawVisualisationResolved,
      isFirstToothActive,
      isLastToothActive,
      isFirstToothInExam,
      isLastToothInExam,
      provider,
      extractedTeethPositions,
      missingTeethPositions,
      allTeethTouched,
      oneToothTouched,
      addPerioExamFeatureEnabled,
      editPerioExamFeatureEnabled,
      cheatSheetOpened,
      isEditing,
      recessionInputType,
      profileId,
    };
    return vm;
  }

  private getActiveJawBoundaryPositions(
    toothOrder: PerioExamTraversalStrategyStep[],
    jawConfig: PerioJawConfigTuple,
    activeJaw: PerioJawConfig['jaw'],
  ): { firstPositionInActiveJaw: number | null; lastPositionInActiveJaw: number | null } {
    const activeJawPositionsInOrder = toothOrder
      .filter((step) => getJawConfigForToothPosition(jawConfig, step.position, step.side).jaw === activeJaw)
      .map((step) => step.position);

    // Deduplicate while keeping traversal order.
    const uniqueActiveJawPositions: number[] = [];
    for (const pos of activeJawPositionsInOrder) {
      if (!uniqueActiveJawPositions.includes(pos)) {
        uniqueActiveJawPositions.push(pos);
      }
    }

    return {
      firstPositionInActiveJaw: uniqueActiveJawPositions[0] ?? null,
      lastPositionInActiveJaw: uniqueActiveJawPositions.at(-1) ?? null,
    };
  }

  /**
   * Returns all the extracted teeth (comes from treatment data) and missing teeth (labeled by the user) positions combined
   *
   * @param missingTeethPositions - Array of tooth numbers marked as missing - needed when the new set of marked teeth haven't got saved yet to state
   */
  private getAllMissingTeethPositions(missingTeethPositions = this.state().missingTeethPositions): number[] {
    const { extractedTeethPositions } = this.state();

    return [...extractedTeethPositions, ...missingTeethPositions];
  }

  private compareTraversalStrategySteps(base: PerioExamTraversalStrategyStep, toCompare: PerioExamTraversalStrategyStep): boolean {
    return base.side === toCompare.side && base.position === toCompare.position;
  }

  /**
   * Injects extracted‑tooth positions into local state *and* removes them from the traversal order.
   *
   * @param extractedTeethPositions - Array of tooth numbers marked as extracted.
   */
  private setExtractedTootPositions(extractedTeethPositions: number[]): void {
    const jawConfig = perioExamJawConfigMap[this.state().traversalStrategy];
    const toothOrderWithExtractedTeethIncluded = getPerioExamTraversalStrategy(jawConfig);
    const { currentToothPosition, toothOrder } = getToothPositionsFilteredByExtracted(
      extractedTeethPositions,
      toothOrderWithExtractedTeethIncluded,
    );
    this.patchState({
      extractedTeethPositions,
      missingTeethPositions: [],
      toothOrder,
      currentToothPosition,
    });
  }

  private moveToTooth(direction: 'next' | 'prev'): void {
    this.shouldMoveToNextToothOnNextPocketDepth = false;
    this.shouldMoveToNextToothOnNextRecession = false;
    this.perioFormControlService.setMovedWithoutValue();
    const { toothOrder: perioExamToothOrder, currentToothPosition: previousToothPosition } = this.state();
    const indexGetter = direction === 'next' ? getNextToothIndex : getPreviousToothIndex;
    const nextDirectionIndex = indexGetter(perioExamToothOrder, previousToothPosition);
    const currentToothPosition = perioExamToothOrder[nextDirectionIndex];

    this.patchStateAndSaveDraft({ currentToothPosition });
  }

  private moveToPosition(position: number | null, relative: boolean, surface: Surface | null): void {
    this.shouldMoveToNextToothOnNextPocketDepth = false;
    this.shouldMoveToNextToothOnNextRecession = false;
    this.perioFormControlService.removePreservedDigit();
    const targetToothPosition = relative ? this.state().currentToothPosition.position + (position ?? 0) : position;
    const { toothOrder: perioExamToothOrder, currentToothPosition } = this.state();
    const targetPosition =
      perioExamToothOrder.find((step) => step.position === targetToothPosition && step.side === currentToothPosition.side) ||
      this.state().currentToothPosition;

    this.patchStateAndSaveDraft({ currentToothPosition: targetPosition });
    if (surface) {
      this.focusOnSpecificSurface(targetPosition, surface);
    } else {
      this.focusOnFirstSurface(targetPosition);
    }
  }

  private moveToSide(side: ToothSide): void {
    this.shouldMoveToNextToothOnNextPocketDepth = false;
    this.shouldMoveToNextToothOnNextRecession = false;
    this.perioFormControlService.removePreservedDigit();

    const { toothOrder: perioExamToothOrder, currentToothPosition } = this.state();
    const targetPosition = perioExamToothOrder.find((step) => step.position === currentToothPosition.position && step.side === side);

    if (targetPosition) {
      this.patchStateAndSaveDraft({ currentToothPosition: targetPosition });
      this.focusOnFirstSurface(targetPosition);
    }
  }

  private focusOnFirstSurface(step: PerioExamTraversalStrategyStep): void {
    const isReversed = isReversedMeasurementOrder(step.position, step.side);
    const measurementOrder = this.perioFormControlService.getMeasurementOrder(isReversed);
    const defaultSurface = measurementOrder[0];
    setTimeout(() => {
      const currentMode = this.perioFormControlService.getInputMode();

      if (currentMode === 'pocketDepth') {
        this.perioFormControlService.setActivePocketDepthCursor(defaultSurface, false, isReversed);
        this.perioFormControlService.setActiveCursor(defaultSurface);
      }
      if (currentMode === 'recession') {
        if (this.isCentralRecession()) {
          this.perioFormControlService.setActiveRecessionCursor(Surface.Central);
          this.perioFormControlService.setActiveCursor(Surface.Central);
        } else {
          this.perioFormControlService.setActiveRecessionCursor(defaultSurface);
          this.perioFormControlService.setActiveCursor(defaultSurface);
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 50);
  }

  private focusOnSpecificSurface(step: PerioExamTraversalStrategyStep, surface: Surface): void {
    const isReversed = isReversedMeasurementOrder(step.position, step.side);
    const measurementOrder = this.perioFormControlService.getMeasurementOrder(isReversed);

    // Verify the target surface exists in the measurement order
    if (!measurementOrder.includes(surface)) {
      console.warn(`Surface ${surface} not found in measurement order, falling back to first surface`);
      this.focusOnFirstSurface(step);
      return;
    }

    setTimeout(() => {
      const currentMode = this.perioFormControlService.getInputMode();

      if (currentMode === 'pocketDepth') {
        this.perioFormControlService.setActivePocketDepthCursor(surface, false, isReversed);
      }
      if (currentMode === 'recession') {
        this.perioFormControlService.setActiveRecessionCursor(surface);
      }
      this.perioFormControlService.setActiveCursor(surface);
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 50);
  }

  private patchStateAndSaveDraft(partialState: Partial<PerioExamState>) {
    this.patchState({ ...partialState });

    this.state$.pipe(debounceTime(0), take(1)).subscribe(this.saveDraft.bind(this));
  }

  private setLoadedFlagWhenDependenciesLoaded(): void {
    const dependenciesLoadedList = [
      this.store.select(selectTeethState).pipe(filter((teethState) => teethState === DataState.Fetched)),
      this.providerService.providersLoaded$,
    ];
    combineLatest(dependenciesLoadedList)
      .pipe(first())
      .subscribe(() => {
        this.patchState({ loaded: true });
      });
  }

  private saveDraft(): void {
    const draft = {
      ...this.state(),
      loaded: false,
    };
    this.localStorageService.saveDraft(draft);
  }

  private isAllSurfacePocketDepthCompleted({ pocketDepth: nullablePd }: PerioMeasurementsBySideDraft): boolean {
    const pocketDepth = nullablePd as PerioMeasurement<number>;
    return isNotNull(pocketDepth.distal) && isNotNull(pocketDepth.central) && isNotNull(pocketDepth.mesial);
  }

  private isSomeSurfacePocketDepthCompleted({ pocketDepth: nullablePd }: PerioMeasurementsBySideDraft): boolean {
    const pocketDepth = nullablePd as PerioMeasurement<number>;
    return isNotNull(pocketDepth.distal) || isNotNull(pocketDepth.central) || isNotNull(pocketDepth.mesial);
  }

  private isShowcase(): boolean {
    return this.siteId() === routes.showcaseSiteId;
  }

  public getDefaultProvider(): Provider | undefined {
    const currentProvider = structuredClone(this.store.selectSignal(selectUserProvider)());
    const currentPrimaryProvider = structuredClone(this.store.selectSignal(selectPatientPrimaryProvider)());

    if (this.isShowcase() && currentProvider) {
      currentProvider.pmsId = `DR_${currentProvider?.firstName.charAt(0).toUpperCase()}${currentProvider?.lastName
        .charAt(0)
        .toUpperCase()}`;
    }

    return currentProvider || currentPrimaryProvider;
  }

  public updatePerioExamProvider(provider: Provider | undefined): void {
    this.patchStateAndSaveDraft({ providerId: provider?.id });
  }

  public setCheatSheetOpenedFlag(cheatSheetOpened: boolean): void {
    this.patchStateAndSaveDraft({ cheatSheetOpened });
  }

  public completePerioExam(isEditing?: boolean): void {
    if (this.isShowcase()) {
      this.store.dispatch(
        patientActions.onAddShowcasePerioExam({
          perioExamTeeth: [{ teeth: this.viewModel().teeth, date: Date.now() }],
        }),
      );

      this.store
        .select(selectSelectedPatientId)
        .pipe(skip(1), distinctUntilChanged(), first())
        .subscribe(() => {
          this.store.dispatch(patientActions.onRemoveShowcasePerioExam());
        });

      this.localStorageService.removeDraft();
    }
    this.perioSavingInProgress.set(true);

    const teeth: PerioExamTooth[] = Object.values(this.state().teethRecord).map((tooth) => mapToothPerioDraftToPerioExamTooth(tooth));
    const baseRequest = {
      date: toDateFormat(new Date(this.state().originalExamData ?? Date.now())),
      teeth,
    };
    const addRequest: AddPerioExamRequest = {
      ...baseRequest,
      providerId: this.state().providerId ?? this.getDefaultProviderId() ?? '',
    };

    const updateRequest: UpdatePerioExamRequest = {
      ...baseRequest,
      newProviderId: this.state().providerId ?? this.getDefaultProviderId() ?? '',
    };

    if (isEditing) {
      this.store.dispatch(patientActions.onUpdatePerioExam({ updateRequest, siteId: this.siteId() ?? undefined }));
    } else {
      this.store.dispatch(patientActions.onAddPerioExam({ addRequest, siteId: this.siteId() ?? undefined }));
    }
  }

  public uploadAudioForPerio = this.effect<{
    audio: BlobPart[];
    logs: string[];
  }>((params$) => {
    return params$.pipe(
      takeUntil(this.destroy$),
      switchMap(async ({ audio, logs }) => {
        const patientId = this.store.selectSignal(selectSelectedPatient)().data?.id ?? '';
        const siteId = this.store.selectSignal(selectUserSite)()?.id ?? '';

        // Validate audio chunks before processing
        if (!this.audioProcessingService.validateAudioChunks(audio)) {
          throw new Error('Invalid audio chunks');
        }

        // Process audio using the dedicated service
        const { file, fileName } = await this.audioProcessingService.processAudioForUpload(audio, siteId, patientId);

        return { file, fileName, logs, siteId, patientId };
      }),
      switchMap(({ file, fileName, logs, siteId, patientId }) => {
        return this.fileUploadService
          .uploadPerioRecording(file, siteId, patientId, {
            fileContentType: 'audio/wav',
            fileName,
            fileSize: file.size,
            logText: logs.join('\n'),
            providerId: this.state().providerId || this.getDefaultProviderId() || '',
          })
          .pipe(
            catchError((error) => {
              console.error('Error uploading perio exam recording:', error);
              this.toastService.error('Failed to upload recording. Please try again.');
              return of(null);
            }),
          );
      }),
    );
  });
}
